import React from 'react';
import { Typography, Box, Paper, Stack, Grid, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const ColorSwatch = ({ name, value, textColor = 'white', description }) => {
  return (
    <Paper 
      elevation={1} 
      sx={{ 
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'gray.g200',
      }}
    >
      <Box
        sx={{
          backgroundColor: value,
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Typography 
          variant="body2" 
          color={textColor}
          fontWeight={600}
          sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
        >
          {name}
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          {value.toUpperCase()}
        </Typography>
        {description && (
          <Typography variant="caption" color="gray.g500">
            {description}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const ColorSection = ({ title, description, colors }) => {
  return (
    <Box sx={{ mb: 6 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body1" color="gray.g600">
            {description}
          </Typography>
        )}
      </Stack>
      <Grid container spacing={3}>
        {colors.map((color, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <ColorSwatch {...color} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const Colors = () => {
  const theme = useTheme();

  const primaryColors = [
    { name: 'Primary Light', value: theme.palette.primary.light, textColor: 'gray.g700', description: 'Light backgrounds' },
    { name: 'Primary Main', value: theme.palette.primary.main, description: 'Primary brand color - Autobia Orange' },
    { name: 'Primary Dark', value: theme.palette.primary.dark, description: 'Hover states, emphasis' },
    { name: 'Primary Medium', value: theme.palette.primary.medium, textColor: 'gray.g700', description: 'Subtle backgrounds' },
  ];

  const secondaryColors = [
    { name: 'Secondary Light', value: theme.palette.secondary.light, textColor: 'gray.g700', description: 'Light backgrounds' },
    { name: 'Secondary Main', value: theme.palette.secondary.main, description: 'Secondary brand color - Autobia Blue' },
    { name: 'Secondary Dark', value: theme.palette.secondary.dark, description: 'Hover states' },
    { name: 'Secondary S500', value: theme.palette.secondary.s500, description: 'Variations' },
  ];

  const neutralColors = [
    { name: 'White', value: theme.palette.white, textColor: 'gray.g700', description: 'Pure white' },
    { name: 'Gray 25', value: theme.palette.gray.g25, textColor: 'gray.g700', description: 'Lightest gray' },
    { name: 'Gray 50', value: theme.palette.gray.g50, textColor: 'gray.g700', description: 'Very light gray' },
    { name: 'Gray 100', value: theme.palette.gray.g100, textColor: 'gray.g700', description: 'Light gray' },
    { name: 'Gray 200', value: theme.palette.gray.g200, textColor: 'gray.g700', description: 'Borders, dividers' },
    { name: 'Gray 400', value: theme.palette.gray.g400, textColor: 'white', description: 'Placeholder text' },
    { name: 'Gray 500', value: theme.palette.gray.g500, textColor: 'white', description: 'Secondary text' },
    { name: 'Gray 600', value: theme.palette.gray.g600, textColor: 'white', description: 'Primary text' },
    { name: 'Gray 700', value: theme.palette.gray.g700, textColor: 'white', description: 'Headings' },
  ];

  const statusColors = [
    { name: 'Success Light', value: theme.palette.success.light, textColor: 'gray.g700', description: 'Success backgrounds' },
    { name: 'Success Main', value: theme.palette.success.main, description: 'Success states - Autobia Green' },
    { name: 'Success Dark', value: theme.palette.success.dark, description: 'Success emphasis' },
    { name: 'Error Light', value: theme.palette.error.light, textColor: 'gray.g700', description: 'Error backgrounds' },
    { name: 'Error Main', value: theme.palette.error.main, description: 'Error states' },
    { name: 'Error Dark', value: theme.palette.error.dark, description: 'Error emphasis' },
    { name: 'Yellow Light', value: theme.palette.yellow.light, textColor: 'gray.g700', description: 'Warning backgrounds' },
    { name: 'Yellow Main', value: theme.palette.yellow.main, textColor: 'gray.g700', description: 'Warning states' },
  ];

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Color System
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Our color palette is carefully designed to reflect the Autobia brand identity while 
          ensuring accessibility and visual harmony across all touchpoints.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="Brand Colors" variant="outlined" />
          <Chip label="WCAG AA Compliant" variant="outlined" />
          <Chip label="Semantic Usage" variant="outlined" />
        </Stack>
      </Stack>

      <ColorSection
        title="Primary Colors"
        description="Autobia Orange - our primary brand color used for key actions and brand elements."
        colors={primaryColors}
      />

      <ColorSection
        title="Secondary Colors"
        description="Autobia Blue - our secondary brand color used for accents and supporting elements."
        colors={secondaryColors}
      />

      <ColorSection
        title="Neutral Colors"
        description="Gray scale colors used for text, backgrounds, and borders throughout the interface."
        colors={neutralColors}
      />

      <ColorSection
        title="Status Colors"
        description="Semantic colors that communicate different states and feedback to users."
        colors={statusColors}
      />

      <Paper sx={{ p: 4, mt: 6, backgroundColor: 'gray.g25' }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Usage Guidelines
        </Typography>
        <Stack spacing={2}>
          <Typography variant="body2">
            <strong>Primary Orange:</strong> Use for primary actions, CTAs, links, and key brand elements.
          </Typography>
          <Typography variant="body2">
            <strong>Secondary Blue:</strong> Use for secondary actions, informational elements, and accents.
          </Typography>
          <Typography variant="body2">
            <strong>Gray Scale:</strong> Use Gray 700-600 for primary text, Gray 500-400 for secondary text, and Gray 200-50 for backgrounds and borders.
          </Typography>
          <Typography variant="body2">
            <strong>Status Colors:</strong> Use Green for success states, Red for errors, and Yellow for warnings.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Colors;